import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { useAuthStore } from '../../auth/store/authStore';
import { FadeInAnimation, Dialog } from '../../../core/components/ui';

interface Props { embedded?: boolean }

const SettingsPage: React.FC<Props> = ({ embedded }) => {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const user = authStore.retrieveUser();
  const prefs = authStore.retrieveLocalPrefs();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const initials = user
    ? `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase()
    : '?';

    const handleLogout = () => {
      setLogoutDialogOpen(false);
    
      authStore.logout();
    
      navigate(ROUTES.LOGIN, {
        replace: true,
      });
    };

  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--color-on-surface-variant)', marginBottom: 8, paddingLeft: 4 }}>
        {title}
      </div>
      <div style={{ background: 'var(--color-surface)', borderRadius: 16, border: '1px solid var(--color-outline-variant)', overflow: 'hidden' }}>
        {children}
      </div>
    </div>
  );

  const Row: React.FC<{ icon: React.ReactNode; label: string; value?: string; onClick?: () => void; danger?: boolean }> = ({ icon, label, value, onClick, danger }) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 14,
        padding: '14px 16px', width: '100%', background: 'none',
        color: danger ? 'var(--color-error)' : 'var(--color-on-surface)',
        borderBottom: '1px solid var(--color-outline-variant)',
        fontSize: 14, fontWeight: 500,
      }}
    >
      <div style={{ color: danger ? 'var(--color-error)' : 'var(--color-on-surface-variant)', flexShrink: 0 }}>{icon}</div>
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      {value && <span style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', marginRight: 4 }}>{value}</span>}
      {onClick && !danger && (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-on-surface-variant)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      )}
    </button>
  );

  const content = (
    <div style={{ padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Profile card */}
      <FadeInAnimation delay={0}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'var(--color-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 20, color: 'white', flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{user?.firstName} {user?.lastName}</div>
            <div style={{ fontSize: 13, color: 'var(--color-on-surface-variant)', marginTop: 2 }}>{user?.email || user?.phoneNumber || ''}</div>
          </div>
        </div>
      </FadeInAnimation>

      {/* Preferences */}
      <FadeInAnimation delay={0.1}>
        <Section title="Preferences">
          <Row
            icon={<PrefsIcon />}
            label="Therapy Preferences"
            value={prefs?.scheduleFor || ''}
            onClick={() => navigate(ROUTES.EDIT_PREFERENCE)}
          />
        </Section>
      </FadeInAnimation>

      {/* Account */}
      {/* <FadeInAnimation delay={0.2}>
        <Section title="Account">
          <Row icon={<HistoryIcon />} label="Payment History" onClick={() => navigate(ROUTES.PAYMENT_HISTORY2)} />
          <Row icon={<SessionIcon />} label="Buy Session Credits" onClick={() => navigate(ROUTES.SESSION)} />
          <Row
            icon={<DeleteIcon />}
            label="Delete Account"
            onClick={() => navigate(ROUTES.DELETE_ACCOUNT)}
            danger
          />
        </Section>
      </FadeInAnimation> */}

      {/* Support */}
      <FadeInAnimation delay={0.3}>
        <Section title="Support">
          <Row
            icon={<LegalIcon />}
            label="Terms & Conditions"
            onClick={() => window.open('https://www.casantey.com/policies', '_blank')}
          />
          <Row
            icon={<ConsentIcon />}
            label="Consent Form"
            onClick={() => window.open('https://www.casantey.com/policies', '_blank')}
          />
        </Section>
      </FadeInAnimation>

      {/* Logout */}
      <FadeInAnimation delay={0.4}>
        <button
          onClick={() => setLogoutDialogOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 20px', borderRadius: 16, width: '100%',
            background: 'var(--color-error-container)',
            color: 'var(--color-error)', fontWeight: 600, fontSize: 15,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Log Out
        </button>
      </FadeInAnimation>

      <FadeInAnimation delay={0.5}>
        <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--color-on-surface-variant)', lineHeight: 1.6 }}>
          TherApp Connect v{process.env.REACT_APP_VERSION || '1.0.0'}{'\n'}
          Powered by Casantey
        </div>
      </FadeInAnimation>
    </div>
  );

  if (embedded) {
    return (
      <>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {content}
        </div>
  
        <Dialog
          open={logoutDialogOpen}
          onClose={() => setLogoutDialogOpen(false)}
        >
          <div
            style={{
              background: 'var(--color-surface)',
              borderRadius: 16,
              padding: 20,
              width: '100%',
              maxWidth: 400,
            }}
          >
            <h3
              style={{
                fontSize: 17,
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              Log Out
            </h3>
  
            <p
              style={{
                fontSize: 13,
                color: 'var(--color-on-surface-variant)',
                marginBottom: 20,
              }}
            >
              Are you sure you want to log out?
            </p>
  
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setLogoutDialogOpen(false)}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 8,
                  background: 'var(--color-surface-variant)',
                  color: 'var(--color-on-surface-variant)',
                  fontWeight: 600,
                }}
              >
                Cancel
              </button>
  
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 8,
                  background: '#ba071f',
                  color: 'white',
                  fontWeight: 700,
                }}
              >
                Log Out
              </button>
            </div>
          </div>
        </Dialog>
      </>
    );
  }

  return (
    <div className="page">
      <div style={{
        height: 60, display: 'flex', alignItems: 'center', padding: '0 16px',
        background: 'var(--color-primary)', gap: 12,
      }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', color: 'white', display: 'flex', alignItems: 'center', padding: 4 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <span style={{ color: 'white', fontWeight: 300, fontSize: 17 }}>Settings</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>{content}</div>

      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
  <div
    style={{
      position: 'relative',
      zIndex: 9999,
      pointerEvents: 'auto',
    }}
  >
    <h3
      style={{
        fontSize: 17,
        fontWeight: 700,
        marginBottom: 8,
      }}
    >
      Log Out
    </h3>

    <p
      style={{
        fontSize: 13,
        color: 'var(--color-on-surface-variant)',
        marginBottom: 20,
      }}
    >
      Are you sure you want to log out?
    </p>

    <div style={{ display: 'flex', gap: 10 }}>
      <button
        type="button"
        onClick={() => setLogoutDialogOpen(false)}
        style={{
          flex: 1,
          height: 44,
          borderRadius: 8,
          background: 'var(--color-surface-variant)',
          color: 'var(--color-on-surface-variant)',
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Cancel
      </button>

      <button
        type="button"
        onClick={handleLogout}
        style={{
          flex: 1,
          height: 44,
          borderRadius: 8,
          background: 'var(--color-error)',
          color: 'white',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Log Out
      </button>
    </div>
  </div>
</Dialog>
    </div>
  );
};

const PrefsIcon: React.FC = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="14" y2="12"/><line x1="4" y1="18" x2="18" y2="18"/></svg>;
const HistoryIcon: React.FC = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
const SessionIcon: React.FC = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 18v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v1h-9a2 2 0 00-2 2v8a2 2 0 002 2h9z"/></svg>;
const DeleteIcon: React.FC = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6m3 0V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>;
const LegalIcon: React.FC = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
const ConsentIcon: React.FC = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;

export default SettingsPage;
